import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { confirm, toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import type { MemberTierDTO } from "../api/client";
import { useDeleteMemberTier, useMemberTiersQuery } from "../api/queries";
import { MemberTierFormDialog } from "../ui/MemberTierFormDialog";
import { MemberTiersTable } from "../ui/MemberTiersTable";
import { MemberTiersToolbar } from "../ui/MemberTiersToolbar";

export function MemberTiersPage() {
  const nav = useNavigate({ from: "/app/customers/tiers" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/customers/tiers",
  });
  const list = useMemberTiersQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });
  const canManage = useActionPermission([
    "customers:create",
    "customers:update",
    "customers:delete",
  ]);
  const deleteTier = useDeleteMemberTier();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTier, setEditingTier] = useState<MemberTierDTO | null>(null);

  const handleCreate = () => {
    setEditingTier(null);
    setDialogOpen(true);
  };

  const handleEdit = (row: MemberTierDTO) => {
    setEditingTier(row);
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: "ລຶບລະດັບສະມາຊິກ",
      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບລະດັບສະມາຊິກນີ້?",
      actionText: "ລຶບ",
      ActionProps: { variant: "destructive" },
    });
    if (ok) {
      toast.promise(deleteTier.mutateAsync(id), {
        loading: "ກຳລັງລຶບ...",
        success: "ລຶບສຳເລັດ",
        error: "ລຶບລົ້ມເຫຼວ",
      });
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ລະດັບສະມາຊິກ" },
          ]}
        />
      </Header>
      <Main>
        <MemberTiersToolbar onCreate={handleCreate} canCreate={canManage} />
        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <MemberTiersTable
            canManage={!!canManage}
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            sortBy={search.sort?.[0]?.field}
            sortOrder={search.sort?.[0]?.dir as "asc" | "desc" | undefined}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </div>
        <MemberTierFormDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          tier={editingTier}
        />
      </Main>
    </>
  );
}
